import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Lock } from 'lucide-react';
import axios from 'axios';

const PageContainer = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 12px;
`;

const ResetCard = styled.div`
  max-width: 400px;
  width: 100%;
  background-color: white;
  padding: 2rem;
  border-radius: 0px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h2`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 1.875rem;
  font-weight: 800;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;
`;

const Form = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  appearance: none;
  border: 1px solid #d1d5db;
  border-radius: 0px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  width: 100%;
  color: #1f2937;

  &:focus {
    outline: none;
    border-color: #47ABDB;
    box-shadow: 0 0 0 3px rgba(71, 171, 219, 0.15);
  }
`;

const Button = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #47ABDB;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(71, 171, 219, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #f87171;
  color: #b91c1c;
  padding: 0.75rem;
  border-radius: 0px;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  background-color: #d1fae5;
  border: 1px solid #34d399;
  color: #065f46;
  padding: 0.75rem;
  border-radius: 0px;
  font-size: 0.875rem;
`;

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTimeoutRef = React.useRef(null);

  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token.');
    }
  }, [token]);

  useEffect(() => {
    return () => clearTimeout(redirectTimeoutRef.current);
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/password/savePassword`, { token, password });
      setSuccess(true);
      // /login (AttendeeLogin) works for every role - LoginForm falls back to
      // a role-based redirect (defaultRedirectFor) when no redirectTo is
      // passed, so there's no need to duplicate that role->path mapping
      // here (that duplication is exactly what caused attendees to be sent
      // to /admin/login before this).
      redirectTimeoutRef.current = setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  if (error && !token) {
    return (
      <PageContainer>
        <ResetCard>
          <ErrorMessage>{error}</ErrorMessage>
        </ResetCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ResetCard>
        <Title>Reset Password</Title>
        <Subtitle>Enter your new password below</Subtitle>
        <Form onSubmit={handleResetPassword}>
          <Input
            id="password"
            name="password"
            type="password"
            required
            minLength={8}
            placeholder="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && (
            <SuccessMessage>
              Password reset successful. Redirecting to login page...
            </SuccessMessage>
          )}

          <Button type="submit" disabled={isLoading || success}>
            <Lock size={20} style={{ marginRight: '0.5rem' }} />
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Form>
      </ResetCard>
    </PageContainer>
  );
};

export default ResetPassword;